import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { SpotlightBottomBarComponent } from '../../../ui-editor/spotlight/shell/bottom-bar.component';
// Was (2026-10-04, plan 5 — the entity panel replaces Spotlight's dock here; the dock markup moved to was-spotlight-dock.html.txt):
// import { SpotlightDockComponent } from '../../../ui-editor/spotlight/shell/dock.component';
import { EntityPanelComponent } from '../panel/entity-panel.component';
import type { CrumbPill, DockMode, DockTenant } from '../../../ui-editor/spotlight/shell/shell.types';
import { SpotlightConversationService } from '../../../ui-editor/spotlight/conversation/conversation.service';
import { StudioNavService } from '../../../../studio-nav/studio-nav.service';
import { NavChoicesComponent } from '../../../../studio-nav/nav-choices.component';
import { GlobalVariablesService } from '../../../../services/global-variables.service';
import { EntityChatSession, type EntityChatPage } from './entity-chat-session';
import { buildEntitySummary, entitiesWithOpen, findSummaryField, type SummaryEntity, type SummaryField } from '../summary/entity-summary';
import { typeLabel } from '../concepts/type-vocabulary';
import type { Entity } from '../../../../model/entity.model';
// Was (2026-10-06, nav 8b Task 4 fix round 1 — the explicit `HistoryEntry[]` return type on `read()`
// went away once `appendOnlySource` (I1) inferred it from its own `map` signature instead):
// import type { HistoryEntry } from '../../../ui-editor/spotlight/shell/history.util';
// Nav 8b Task 4 fix round 1 (I1, I2) — both chats share one append-only wrapper over their messages
// (even though `session.messages` here is itself append-only, unlike Spotlight's), and `Where` for the
// place a turn started at.
import { appendOnlySource, type Where } from '../../../../studio-nav/history';

/**
 * THE ENTITY CHAT ON SCREEN (ZS ENTITY, plan 3b) — Spotlight's own bottom bar and dock, and a reply card
 * with Spotlight's card classes (pinned), so the two studio chats look the same (Q3). Always present on the
 * entity page (Q4).
 * Was (2026-10-15): "... entity page (Q4) and dark in both page modes."
 * It follows the Entities page's sun/moon switch: dark on the dark page, exactly as before, and the white bar, card,
 * panel button and the panel itself on the white one, so the chat matches the screen it sits on.
 * It decides NOTHING (ADR 0008): every action is a call on `EntityChatSession`; this only renders.
 */
@Component({
  selector: 'app-zs-entity-chat',
  standalone: true,
  // Was (2026-10-04, plan 5 — the entity panel replaces Spotlight's dock here): imports: [CommonModule, SpotlightBottomBarComponent, SpotlightDockComponent],
  // Was (2026-10-05, nav 8a Task 4 — the navigator's own buttons are new here): imports: [CommonModule, SpotlightBottomBarComponent, EntityPanelComponent],
  imports: [CommonModule, SpotlightBottomBarComponent, EntityPanelComponent, NavChoicesComponent],
  templateUrl: './zs-entity-chat.component.html',
})
export class ZsEntityChatComponent implements OnInit, OnDestroy {
  @Input({ required: true }) page!: EntityChatPage;
  @ViewChild('cardTpl', { static: true }) cardTpl!: TemplateRef<unknown>;

  private readonly conversation = inject(SpotlightConversationService);
  private readonly nav = inject(StudioNavService);
  private readonly globals = inject(GlobalVariablesService);
  /** The Entities page's sun/moon switch, read on every check: it is a plain field nothing can watch. */
  get pageThemeDark(): boolean { return this.globals.pageThemeDark; }
  session!: EntityChatSession;
  // Was (2026-10-06, nav 8b Task 4 — the bar's text is now the one shared draft, not this chat's own field):
  // input = '';
  get input(): string { return this.nav.draft(); }
  set input(v: string) { this.nav.draft.set(v); }
  /** Nav 8b Task 4 — this chat's attach id into the tab's one history. */
  private historyId: number | null = null;
  /** Nav 8b Task 4 fix round 1 (I2) — the place the CURRENT turn started at, captured before it runs so
   * the history tags it correctly even if the open entity changes before this chat's own watch
   * callback gets to sync (e.g. Finalize closes the editor mid-turn). Null when no turn is in flight. */
  private turnWhere: Where | null = null;
  dockTenant: DockTenant = null;
  /** Final review (2026-10-03, J6) — the dock's own mode and pin, as Spotlight's dock already offers. */
  // Unused since plan 5 (the entity panel replaced the dock) — kept, not deleted.
  dockMode: DockMode = 'docked';
  // Unused since plan 5 (the entity panel replaced the dock) — kept, not deleted.
  dockPinned = false;
  /** The field picked in the Tree tab, shown in the Properties tab. */
  selected: { entity: string; path: string[]; field: SummaryField } | null = null;
  /** Plan 5 — the picked field, as the panel's focus. A FIELD set once per pick, never a getter: a fresh object on every
   * change detection would re-fire the panel's ngOnChanges and pin the inspector to the field (Back would not work). */
  panelFocus: { key: string; path: string[]; name: string } | null = null;
  progressSeconds = 0;
  /** Nav 7b T5 review (C1) — true only while the navigator is being asked; a second submit in that window must wait. */
  routing = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private unwatch: (() => void) | null = null;
  /** Nav 8c (Q33) — "Do it here": the navigator pings this lane the instant it hands the sentence back. */
  private handoffPingSub: Subscription | null = null;
  readonly typeLabel = typeLabel;

  ngOnInit(): void {
    this.session = new EntityChatSession(
      this.page,
      // studioChat is typed for Spotlight's hop body; the entity chat's body is the same wire shape.
      (body, signal) => this.conversation.postHop(body as never, signal),
      (clientId, record) => this.conversation.sendTurnRecord(clientId, record as never),
      `cnv_${Math.random().toString(36).slice(2, 10)}`,
      () => `req_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    );
    // Was (2026-10-30 — the slot now belongs to the entity page, so the measure needs no chat and a chat going away
    // can't blank it):
    // this.nav.setLeaveGuard(() => {
    //   const n = this.session.unsavedCount();
    //   return n > 0 ? { count: n, name: this.page.open()?.name || 'The new entity' } : null;
    // });
    // This lane's note.
    this.nav.setLane('entity');
    // Nav 8a (Q18b) — the open entity is "here": never offered as somewhere to go.
    this.nav.setHere(() => { const o = this.page.open(); return o?.id ? { kind: 'entity', id: o.id } : null; });
    // Nav 8b final review (M3) — a history tag does not open anything while this chat is mid-turn.
    this.nav.setBusyCheck(() => this.session.busy || this.routing);
    // Was (2026-10-06, nav 8b Task 4 — a raw `read()` over `session.messages`, filtering fromNav lines
    // inline; superseded by the append-only wrapper both chats now share (I1), and `where()` always
    // read the LIVE open entity, which fix round 1 (I2) found tags a turn with the wrong place when the
    // open entity changes before the chat's own watch callback gets to sync):
    // this.historyId = this.nav.attach({
    //   where: () => {
    //     const o = this.page.open();
    //     return o?.id
    //       ? { lane: 'entity', label: `on Data · ${o.name || 'New entity'}`, target: { kind: 'entity', object: { kind: 'entity', id: o.id, name: o.name } } }
    //       : { lane: 'entity', label: 'on Data', target: { kind: 'data' } };
    //   },
    //   read: (): HistoryEntry[] => this.session.messages
    //     .filter((m) => !m.fromNav)
    //     .map((m) => ({ role: m.role, text: m.text, group: m.requestId, notice: m.notice })),
    // });
    // Nav 8b (Q17) — this chat's turns join the tab's one history, tagged with the open entity.
    // Nav 8b Task 4 fix round 1 (I2) — a turn in flight is tagged with where it STARTED (`turnWhere`);
    // only when none is in flight does `where()` fall back to the live open entity.
    this.historyId = this.nav.attach(appendOnlySource(
      () => this.session.messages,
      (m) => (m.fromNav ? null : { role: m.role, text: m.text, group: m.requestId, notice: m.notice }),
      () => this.turnWhere ?? this.currentWhere(),
    ));
    this.tryHandoff();
    // Nav 8c (Q33) — "Do it here" on an unsure go/stay card: the ping runs this instant, not on the next poll.
    this.handoffPingSub = this.nav.handoffPing$.subscribe((lane) => { if (lane === 'entity') this.tryHandoff(); });
    // Was (2026-10-05, nav 7b T5 — RULED: the watch callback also runs the rest of a "go and do" once the page catches up):
    // this.unwatch = this.page.watch(() => setTimeout(() => this.session.checkWaiting(), 0));
    // Was (2026-10-06, nav 8b Task 4 fix round 2 — the premise was wrong: chatWatch fires AFTER the
    // open entity has already changed, so syncHistory() running "before" the tag changes was never what
    // made a turn's tag correct — "the shared history must copy this chat's turns in BEFORE the open
    // entity's tag changes, or a turn about the entity just closed is tagged with the next one"):
    // this.unwatch = this.page.watch(() => { setTimeout(() => this.session.checkWaiting(), 0); this.tryHandoff(); });
    // Nav 8b Task 4 fix round 2 — this callback's own syncHistory() just copies in whatever is pending;
    // the place a turn is tagged with comes from `turnWhere`, captured when the turn started (I2), not
    // from this callback running before or after the page's own change.
    this.unwatch = this.page.watch(() => {
      this.nav.syncHistory();
      setTimeout(() => this.session.checkWaiting(), 0);
      this.tryHandoff();
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
    // Was (2026-10-03, plan 4a final review — leaving the page drops waiting edits too, on the record):
    // this.session?.cancel();
    this.session?.dispose();
    this.unwatch?.();
    // Nav 8c (Q33) — the ping subscription does not outlive this chat.
    this.handoffPingSub?.unsubscribe();
    // Nav 8b Task 4 — this chat's turns so far stay in the tab's history; later ones are not.
    this.nav.detach(this.historyId);
    this.historyId = null;
    this.nav.setLane(null);
    this.nav.setHere(null);
    // Nav 8b final review (M3).
    this.nav.setBusyCheck(null);
  }

  /** Nav 8b Task 4 fix round 1 (I2) — where the builder is now: used both as the history source's
   * fallback (no turn in flight) and to capture `turnWhere` at the start of a turn. */
  private currentWhere(): Where {
    const o = this.page.open();
    return o?.id
      ? { lane: 'entity', label: `on Data · ${o.name || 'New entity'}`, target: { kind: 'entity', object: { kind: 'entity', id: o.id, name: o.name } } }
      : { lane: 'entity', label: 'on Data', target: { kind: 'data' } };
  }

  /** Nav 7b (Q14) — the rest of a "go and do" sentence, once the entity it was for is open (or at once, for the Data page). */
  private tryHandoff(): void {
    const h = this.nav.peekHandoff('entity');
    // Was (2026-10-05, nav 7b live check, L3 — the hand-off's own `chatWatch` fires INSIDE the still-running
    // onSubmit (routing = true), so it was taken here and then lost at onSubmit's `if (this.routing ||
    // this.session.busy) return;`): if (!h || this.session.busy) return;
    if (!h || this.session.busy || this.routing) return;
    if (h.entityId && this.page.open()?.id !== h.entityId) return;
    this.nav.takeHandoff('entity');
    // Was (2026-10-06, nav 8c Task 4 — a "Do it here" hand-off still ran through the navigator's own
    // intercept, which could ask Jev again and loop the go/stay card): void this.onSubmit(h.text);
    // Nav 8c (Q33) — `h.here` skips the navigator entirely: "Do it here" must never be asked about twice.
    void this.onSubmit(h.text, !!h.here);
  }

  /** The field picked in the Tree, as path + name, when it belongs to the open entity — "this" for a field. */
  selectedPath(): string[] | null {
    const s = this.selected;
    return s && this.page.open()?.name === s.entity ? [...s.path, s.field.name] : null;
  }

  /** Nav 8a (Q18a) — the navigator asked here: its card takes the bar's card slot. */
  get navAsks(): boolean { const c = this.nav.laneCard('entity'); return !!c && !c.offer; }

  get crumbs(): CrumbPill[] {
    const open = this.page.open();
    const root: CrumbPill = { label: 'Entities', jsonPath: '', kind: 'page' };
    return open ? [root, { label: open.name || 'New entity', jsonPath: open.id, kind: 'section' }] : [root];
  }

  get unsaved(): number | null {
    return this.session.unsavedCount() || null;
  }

  get summary(): SummaryEntity[] {
    // Was (2026-10-03, final review — J3, the Tree never showed the open entity's unsaved edits):
    // return buildEntitySummary(this.page.entities());
    return buildEntitySummary(entitiesWithOpen(this.page.entities(), this.page.open()));
  }

  // Unused since plan 5 (the entity panel replaced the dock) — kept, not deleted.
  get dockTitle(): string {
    return this.dockTenant === 'style' && this.selected ? `${this.selected.entity} › ${this.selected.field.name}` : '';
  }

  /** Edit opens the page's own form, only for a field of the open entity. */
  // Unused since plan 5 (the entity panel replaced the dock) — kept, not deleted.
  get canEditSelected(): boolean {
    return !!this.selected && this.page.open()?.name === this.selected.entity && this.selected.field.type !== 'ARRAY_OF_OBJECT';
  }

  // Was (2026-10-06, nav 8c Task 4 — a "Do it here" hand-off had no way to skip the navigator, so it
  // ran the intercept again and could ask Jev a second time, or re-trip the go/stay card):
  // async onSubmit(text: string): Promise<void> {
  /** Nav 8c (Q33) — `skipNav`: a "Do it here" hand-off runs straight to this chat's own send, never the navigator. */
  async onSubmit(text: string, skipNav = false): Promise<void> {
    // Was (2026-10-05, nav 7b T5 review, C1 — only `session.busy` was checked here, which the navigator
    // intercept below runs ahead of: a second message typed while it was being asked slipped through and
    // silently dropped the first): (no equivalent check existed here)
    if (this.routing || this.session.busy) return;
    // Nav 8b Task 4 fix round 1 (I2) — this turn is tagged with where it STARTED: the open entity can
    // change (e.g. Finalize closes the editor) before this chat's own watch callback gets to sync.
    this.turnWhere = this.currentWhere();
    this.input = '';
    this.startTimer();
    // Nav 7b (Q13, R1) — the navigator first: it takes only clear navigation; a note it has for this lane shows here.
    // The timer was already running above, so a real intercept (which can call Jev) shows it too; the early
    // returns below stop it since no outer `finally` covers them — `routing`'s own `finally` does (C1).
    // M2 — skip the navigator while an approval is pending: the sentence must reach the held action's card,
    // never a `tell()` notice that would replace it.
    // Nav 8c (Q33) — and skip it on a "Do it here" hand-off: the builder already answered go/stay.
    // Was (2026-10-06, nav 8c Task 4): if (!this.session.pendingAction) {
    if (!skipNav && !this.session.pendingAction) {
      this.routing = true;
      try {
        const nav = await this.nav.intercept(text);
        // Was (2026-10-06, nav 8b Task 4 fix round 2 (controller ruling) — none of these three returns
        // reaches the end-of-turn sync below, so `turnWhere` was left set until the NEXT onSubmit call
        // overwrote it; harmless in practice (nothing these branches push to `session.messages` needs
        // the stale tag), but a leftover value is still cleared here, on the record):
        // if (nav.kind === 'went') { this.stopTimer(); return; }
        // if (nav.kind === 'say') { this.stopTimer(); this.session.tell(text, nav.text); return; }
        // if (nav.kind === 'asked') { this.stopTimer(); return; }
        if (nav.kind === 'went') { this.stopTimer(); this.turnWhere = null; return; }
        if (nav.kind === 'say') { this.stopTimer(); this.session.tell(text, nav.text); this.turnWhere = null; return; }
        // Nav 8a (Q18a) — the navigator asked with buttons; the sentence is not this chat's.
        if (nav.kind === 'asked') { this.stopTimer(); this.turnWhere = null; return; }
      } finally {
        this.routing = false;
        // Nav 7b live check (L3) — a hand-off that arrived during routing (tryHandoff's own guard above held
        // it back) runs now, as soon as routing ends, instead of being lost.
        this.tryHandoff();
      }
    }
    // Was (2026-10-03, plan 4a — the field picked in the Tree goes with the sentence): try { await this.session.send(text); } finally { this.stopTimer(); }
    // Was (2026-10-05, nav 8a Task 4 — no settle afterward, so a decline with the name elsewhere had no "Or go to:"):
    // try { await this.session.send(text, this.selectedPath()); } finally { this.stopTimer(); }
    try { await this.session.send(text, this.selectedPath()); } finally { this.stopTimer(); }
    // Nav 8a (Q18b) — a turn that declined ("There's no entity called X") gets "Or go to:" if X is elsewhere.
    this.nav.settle('entity', this.session.lastStepKind === 'decline');
    // Nav 8b Task 4 — this turn's new lines join the tab's one history.
    this.nav.syncHistory();
    // Nav 8b Task 4 fix round 1 (I2) — the turn is over; the source falls back to the live place again.
    this.turnWhere = null;
  }

  async choose(id: string): Promise<void> {
    // Nav 8b Task 4 fix round 1 (I2) — see onSubmit: this turn is tagged with where it started.
    this.turnWhere = this.currentWhere();
    this.startTimer();
    try { await this.session.choose(id); } finally { this.stopTimer(); }
    // Nav 8b Task 4 — this turn's new lines join the tab's one history.
    this.nav.syncHistory();
    this.turnWhere = null;
  }

  // Was (2026-10-03, final review — J6, Save from the reminder showed no progress timer):
  // async saveNow(): Promise<void> {
  //   await this.session.saveNow();
  // }
  async saveNow(): Promise<void> {
    // Nav 8b Task 4 fix round 1 (I2) — see onSubmit: this turn is tagged with where it started.
    this.turnWhere = this.currentWhere();
    this.startTimer();
    try { await this.session.saveNow(); } finally { this.stopTimer(); }
    // Nav 8b Task 4 — this turn's new lines join the tab's one history.
    this.nav.syncHistory();
    this.turnWhere = null;
  }

  openDock(tenant: DockTenant): void {
    this.dockTenant = tenant;
  }

  /** Plan 6 (Q54) — the panel's own button: open it on the Entities tab, or close it. */
  togglePanel(): void {
    this.dockTenant = this.dockTenant ? null : 'tree';
  }

  // Unused since plan 5 (the entity panel replaced the dock) — kept, not deleted.
  select(entity: string, path: string[], field: SummaryField): void {
    this.selected = { entity, path, field };
    this.dockTenant = 'style';
  }

  // Unused since plan 5 (the entity panel replaced the dock) — kept, not deleted.
  editSelected(): void {
    if (this.selected && this.canEditSelected) this.page.editField(this.selected.path, this.selected.field.name);
  }

  /** Plan 5 — a field picked in the panel becomes the chat's "this" (the same `selected` the Tree used to set). */
  pickFromPanel(e: { key: string; entity: string; path: string[]; name: string }): void {
    const field = findSummaryField(this.summary, e.entity, e.path, e.name);
    if (!field) return;
    this.selected = { entity: e.entity, path: e.path, field };
    this.panelFocus = { key: e.key, path: e.path, name: e.name };
  }

  /** Plan 5 — Open on page: the page's own open path, only when the open entity has no unsaved changes. */
  openOnPage(entity: Entity): void {
    if (this.page.open() && this.session.unsavedCount() > 0) return;
    this.page.adopt(JSON.parse(JSON.stringify(entity)) as Entity, 'existing');
  }

  /** N18 (plan 4b) — the picked field as it is now, re-read from the summary, never the copy taken at pick time. */
  // Unused since plan 5 (the entity panel replaced the dock) — kept, not deleted.
  get selectedField(): SummaryField | null {
    const s = this.selected;
    return s ? findSummaryField(this.summary, s.entity, s.path, s.field.name) : null;
  }

  /** Plan 4b — the confirm bar's approve, with the same live timer as a send. */
  async approve(): Promise<void> {
    // Nav 8b Task 4 fix round 1 (I2) — see onSubmit: this turn is tagged with where it started.
    this.turnWhere = this.currentWhere();
    this.startTimer();
    try { await this.session.approve(); } finally { this.stopTimer(); }
    // Nav 8b Task 4 — this turn's new lines join the tab's one history.
    this.nav.syncHistory();
    this.turnWhere = null;
  }

  private startTimer(): void {
    this.progressSeconds = 0;
    this.stopTimer();
    this.timer = setInterval(() => this.progressSeconds++, 1000);
  }

  private stopTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}
